import { useEffect, useMemo, useRef, useState } from "react";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { Modal, ConfirmDialog } from "../components/Modal.jsx";
import { EmptyState } from "../components/EmptyState.jsx";
import { IncomeExpenseChart, CategoryDonut } from "../components/Charts.jsx";
import { WalletIcon, PlusIcon, UploadIcon, TrashIcon, CheckIcon, SettingsIcon } from "../components/Icons.jsx";
import * as Finance from "../lib/finance.js";
import * as Pdf from "../lib/pdftext.js";

// These ids are the ones lib/finance.js switches on — anything else silently
// falls through to per-day buckets.
const GRANULARITIES = [
  { id: "day", label: "Daily" },
  { id: "week", label: "Weekly" },
  { id: "fortnight", label: "Fortnightly" },
  { id: "month", label: "Monthly" },
  { id: "quarter", label: "Quarterly" },
  { id: "annual", label: "Annual" },
];

const money = (n, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 })
    .format(n || 0);

export function FinancialsView() {
  const { accounts, transactions, put, remove, setting, patchSetting, SETTING_IDS } = useVault();
  const toast = useToast();

  const [accountId, setAccountId] = useState(null);
  const [gran, setGran] = useState("month");
  const [range, setRange] = useState({ from: "", to: "" });
  const [institution, setInstitution] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);

  const prefs = setting(SETTING_IDS.preferences, {});
  const currency = prefs.currency || "USD";

  // Everyone starts with one account so imports have somewhere to land.
  useEffect(() => {
    if (accounts.length === 0) {
      put("account", { name: "Personal", createdAt: Date.now() }).catch(() => {});
    }
  }, [accounts.length, put]);

  const active = useMemo(
    () => accounts.find((a) => a.id === accountId) || accounts[0] || null,
    [accounts, accountId]
  );

  const scoped = useMemo(
    () => transactions.filter((t) => t.accountId === active?.id),
    [transactions, active]
  );

  const institutions = useMemo(
    () => [...new Set(scoped.map((t) => t.institution).filter(Boolean))].sort(),
    [scoped]
  );

  const filtered = useMemo(() => {
    let list = scoped;
    if (institution !== "all") list = list.filter((t) => t.institution === institution);
    if (range.from) list = list.filter((t) => t.date >= range.from);
    if (range.to) list = list.filter((t) => t.date <= range.to);
    return list;
  }, [scoped, institution, range]);

  const buckets = useMemo(() => Finance.aggregate(filtered, gran), [filtered, gran]);
  const totals = useMemo(() => {
    const income = filtered.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const expense = filtered.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    return { income, expense, net: income - expense };
  }, [filtered]);

  const expenseByCat = useMemo(() => Finance.categoryTotals(filtered, "expense"), [filtered]);
  const incomeByCat = useMemo(() => Finance.categoryTotals(filtered, "income"), [filtered]);

  if (!active) {
    return <EmptyState icon={WalletIcon} title="Setting up your first account…" />;
  }

  return (
    <div className="col" style={{ gap: "var(--s-5)" }}>
      {/* --- accounts ------------------------------------------------------ */}
      <div className="row between wrap" style={{ gap: "var(--s-3)" }}>
        <div className="tabs grow">
          {accounts.map((a) => (
            <button key={a.id} className={`tab ${a.id === active.id ? "active" : ""}`}
              onClick={() => setAccountId(a.id)}
              onDoubleClick={() => setRenaming(a)}>
              <WalletIcon size={14} /> {a.name}
            </button>
          ))}
          <button className="tab" onClick={() => setRenaming({ name: "" })}>
            <PlusIcon size={14} /> Account
          </button>
        </div>

        <div className="row" style={{ gap: "var(--s-2)" }}>
          <button className="btn btn-ghost" onClick={() => setShowCategories(true)}>
            <SettingsIcon size={15} /> Categories
          </button>
          <button className="btn btn-primary" onClick={() => setShowImport(true)}>
            <UploadIcon size={15} /> Import
          </button>
        </div>
      </div>

      {scoped.length === 0 ? (
        <EmptyState icon={WalletIcon} title={`${active.name} has no transactions yet`}
          action={
            <button className="btn btn-primary" onClick={() => setShowImport(true)}>
              <UploadIcon size={15} /> Import a statement
            </button>
          }>
          Import CSV, Excel or PDF from your bank. Everything is parsed on this device and
          encrypted before it is stored — the file never leaves your machine.
        </EmptyState>
      ) : (
        <>
          {/* --- filters ---------------------------------------------------- */}
          <div className="row wrap" style={{ gap: "var(--s-3)" }}>
            <div className="segmented">
              {GRANULARITIES.map((g) => (
                <button key={g.id} aria-pressed={gran === g.id} onClick={() => setGran(g.id)}>
                  {g.label}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: "var(--s-2)" }}>
              <input type="date" value={range.from} style={{ width: 150 }}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} aria-label="From date" />
              <span className="muted small">to</span>
              <input type="date" value={range.to} style={{ width: 150 }}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} aria-label="To date" />
              {(range.from || range.to) && (
                <button className="btn btn-sm btn-ghost" onClick={() => setRange({ from: "", to: "" })}>
                  Clear
                </button>
              )}
            </div>
            {institutions.length > 1 && (
              <select value={institution} onChange={(e) => setInstitution(e.target.value)} style={{ width: 180 }}>
                <option value="all">All institutions</option>
                {institutions.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            )}
          </div>

          {/* --- summary ---------------------------------------------------- */}
          <div className="stat-row">
            <div className="stat">
              <div className="label">Income</div>
              <div className="value pos">{money(totals.income, currency)}</div>
            </div>
            <div className="stat">
              <div className="label">Expenses</div>
              <div className="value neg">{money(totals.expense, currency)}</div>
            </div>
            <div className="stat">
              <div className="label">Net</div>
              <div className={`value ${totals.net >= 0 ? "pos" : "neg"}`}>{money(totals.net, currency)}</div>
            </div>
            <div className="stat">
              <div className="label">Transactions</div>
              <div className="value">{filtered.length}</div>
            </div>
          </div>

          {/* --- charts ----------------------------------------------------- */}
          {buckets.length > 0 && (
            <div className="panel">
              <div className="panel-title">Income vs expenses</div>
              <div style={{ overflowX: "auto" }}>
                <IncomeExpenseChart buckets={buckets} currency={currency} />
              </div>
            </div>
          )}

          <div className="view-split" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {expenseByCat.length > 0 && (
              <div className="panel">
                <CategoryDonut data={expenseByCat} title="Expenses by category" currency={currency} />
              </div>
            )}
            {incomeByCat.length > 0 && (
              <div className="panel">
                <CategoryDonut data={incomeByCat} title="Income by category" currency={currency} />
              </div>
            )}
          </div>

          {/* --- transactions ----------------------------------------------- */}
          <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
            <div className="panel-title" style={{ padding: "var(--s-4) var(--s-5) 0" }}>
              Transactions
            </div>
            <div style={{ maxHeight: 520, overflowY: "auto" }}>
              {buckets.slice().reverse().map((b) => (
                <div key={b.key}>
                  <div className="row between" style={{
                    padding: "var(--s-3) var(--s-5)",
                    background: "var(--surface-2)",
                    borderTop: "1px solid var(--border-subtle)",
                    position: "sticky", top: 0, zIndex: 1,
                  }}>
                    <strong className="small">{b.label}</strong>
                    <span className="small">
                      <span style={{ color: "var(--success)" }}>+{money(b.income, currency)}</span>
                      {"  "}
                      <span style={{ color: "var(--danger)" }}>−{money(b.expense, currency)}</span>
                    </span>
                  </div>
                  <table className="table">
                    <tbody>
                      {b.txns.slice().sort((x, y) => y.date.localeCompare(x.date)).map((t) => (
                        <tr key={t.id} onClick={() => setEditingTxn(t)}>
                          <td className="muted tiny nowrap" style={{ width: 92 }}>{t.date}</td>
                          <td className="truncate">{t.description}</td>
                          <td style={{ width: 150 }}><span className="tag">{t.category}</span></td>
                          <td className={`num ${t.type === "income" ? "pos" : "neg"}`} style={{ width: 120 }}>
                            {t.type === "income" ? "+" : "−"}{money(t.amount, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* --- dialogs --------------------------------------------------------- */}
      {showImport && (
        <ImportDialog account={active} onClose={() => setShowImport(false)} />
      )}

      {editingTxn && (
        <TransactionEditor txn={editingTxn} currency={currency}
          categories={setting(SETTING_IDS.categories, null) || Finance.defaultCategories()}
          onClose={() => setEditingTxn(null)} />
      )}

      {showCategories && (
        <CategoryManager onClose={() => setShowCategories(false)} />
      )}

      {renaming && (
        <AccountDialog account={renaming} canDelete={accounts.length > 1}
          onDelete={() => { setConfirmDeleteAccount(true); }}
          onClose={() => setRenaming(null)} />
      )}

      {confirmDeleteAccount && renaming?.id && (
        <ConfirmDialog title={`Delete "${renaming.name}"?`} danger confirmLabel="Delete account"
          message={`This removes the account and its ${scoped.length} transactions from every device you sync with.`}
          onCancel={() => setConfirmDeleteAccount(false)}
          onConfirm={async () => {
            for (const t of transactions.filter((t) => t.accountId === renaming.id)) await remove(t.id);
            await remove(renaming.id);
            setConfirmDeleteAccount(false);
            setRenaming(null);
            setAccountId(null);
            toast.success("Account deleted");
          }} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function AccountDialog({ account, canDelete, onDelete, onClose }) {
  const { put } = useVault();
  const toast = useToast();
  const [name, setName] = useState(account.name || "");

  const save = async () => {
    if (!name.trim()) return toast.warn("Give the account a name.");
    await put("account", { name: name.trim(), createdAt: account.createdAt || Date.now() }, account.id || null);
    toast.success(account.id ? "Account renamed" : "Account created");
    onClose();
  };

  return (
    <Modal title={account.id ? "Rename account" : "New account"} size="narrow" onClose={onClose}
      footer={
        <>
          {account.id && canDelete && (
            <button className="btn btn-danger" onClick={onDelete}><TrashIcon size={15} /> Delete</button>
          )}
          <div className="grow" />
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save</button>
        </>
      }>
      <div className="field">
        <label htmlFor="acc-name">Account name</label>
        <input id="acc-name" type="text" value={name} autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="e.g. Personal, Business 1" />
      </div>
      <p className="hint-text">
        Each account keeps its own transactions, summary and charts. Imports land in
        whichever account is selected.
      </p>
    </Modal>
  );
}

function ImportDialog({ account, onClose }) {
  const { put, setting, patchSetting, SETTING_IDS } = useVault();
  const toast = useToast();
  const fileRef = useRef(null);

  const [step, setStep] = useState("pick");
  const [institution, setInstitution] = useState("");
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);

  const rules = setting(SETTING_IDS.categoryRules, {});

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const name = file.name.toLowerCase();

      if (name.endsWith(".csv") || name.endsWith(".txt")) {
        const parsed = Finance.parseCSV(await file.text());
        startMapping(parsed);
      } else if (name.endsWith(".xlsx")) {
        const parsed = await Finance.parseXLSX(await file.arrayBuffer());
        startMapping(parsed);
      } else if (name.endsWith(".pdf")) {
        const text = await Pdf.extractText(await file.arrayBuffer());
        if (!text || text.trim().length < 40) {
          throw new Error(
            "No text could be read from that PDF — it's likely a scanned image. Export CSV from your bank instead."
          );
        }
        const txns = Finance.parsePdfText(text, { rules });
        if (!txns.length) throw new Error("No transactions were recognised in that PDF.");
        setPreview(txns);
        setStep("confirm");
      } else {
        throw new Error("Import a .csv, .xlsx or .pdf file.");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startMapping = (parsed) => {
    if (parsed.length < 2) throw new Error("That file has no data rows.");
    setRows(parsed);
    setMapping(Finance.guessColumns(parsed[0]));
    setStep("map");
  };

  const buildPreview = () => {
    const txns = Finance.rowsToTransactions(rows.slice(1), mapping, { institution, rules });
    if (!txns.length) return toast.error("No rows could be read with that column mapping.");
    setPreview(txns);
    setStep("confirm");
  };

  const commit = async () => {
    setBusy(true);
    try {
      for (const t of preview) {
        await put("transaction", { ...t, accountId: account.id, institution: t.institution || institution });
      }
      toast.success(`Imported ${preview.length} transactions into ${account.name}`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const header = rows[0] || [];

  return (
    <Modal title={`Import into ${account.name}`} size="wide" onClose={onClose}
      subtitle={step === "confirm" ? `${preview.length} transactions ready` : "CSV, Excel or PDF — parsed on this device"}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <div className="grow" />
          {step === "map" && (
            <button className="btn btn-primary" onClick={buildPreview} disabled={!mapping}>Preview</button>
          )}
          {step === "confirm" && (
            <button className="btn btn-primary" onClick={commit} disabled={busy}>
              {busy ? <span className="spinner" /> : <CheckIcon size={15} />} Import {preview.length}
            </button>
          )}
        </>
      }>
      {step === "pick" && (
        <>
          <div className="field">
            <label htmlFor="imp-inst">Financial institution</label>
            <input id="imp-inst" type="text" value={institution} autoFocus
              onChange={(e) => setInstitution(e.target.value)}
              placeholder="e.g. Commonwealth Bank, Chase" />
          </div>

          <button className="btn btn-ghost btn-block btn-lg" disabled={busy}
            onClick={() => fileRef.current?.click()}
            style={{ padding: "var(--s-8)", borderStyle: "dashed", flexDirection: "column", gap: "var(--s-2)" }}>
            {busy ? <span className="spinner" /> : <UploadIcon size={22} />}
            <span>Choose a statement file</span>
            <span className="tiny muted">.csv · .xlsx · .pdf</span>
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.pdf" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />

          <p className="hint-text">
            Files are decoded in your browser — Excel is unzipped with the browser's own
            decompression and PDF text is extracted from the file's content streams.
            Nothing is uploaded.
          </p>
        </>
      )}

      {step === "map" && mapping && (
        <>
          <p className="small secondary">
            Confirm which column is which. AIDIA guessed from the header row.
          </p>
          <div className="field-row wrap">
            {[
              ["date", "Date"],
              ["description", "Description"],
              ["amount", "Amount"],
              ["debit", "Debit (money out)"],
              ["credit", "Credit (money in)"],
            ].map(([key, label]) => (
              <div className="field" key={key} style={{ minWidth: 160 }}>
                <label>{label}</label>
                <select value={mapping[key]} onChange={(e) => setMapping({ ...mapping, [key]: Number(e.target.value) })}>
                  <option value={-1}>— none —</option>
                  {header.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                </select>
              </div>
            ))}
          </div>
          <p className="hint-text">
            Use <strong>Amount</strong> for a single signed column, or <strong>Debit</strong> and{" "}
            <strong>Credit</strong> when your bank splits them.
          </p>
        </>
      )}

      {step === "confirm" && (
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          <table className="table">
            <thead>
              <tr><th>Date</th><th>Description</th><th>Category</th><th className="num">Amount</th></tr>
            </thead>
            <tbody>
              {preview.slice(0, 80).map((t) => (
                <tr key={t.id}>
                  <td className="tiny muted nowrap">{t.date}</td>
                  <td className="truncate" style={{ maxWidth: 280 }}>{t.description}</td>
                  <td><span className="tag">{t.category}</span></td>
                  <td className={`num ${t.type === "income" ? "pos" : "neg"}`}>
                    {t.type === "income" ? "+" : "−"}{t.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.length > 80 && <p className="hint-text">…and {preview.length - 80} more.</p>}
        </div>
      )}
    </Modal>
  );
}

function TransactionEditor({ txn, categories, currency, onClose }) {
  const { put, remove, setting, patchSetting, SETTING_IDS } = useVault();
  const toast = useToast();
  const [draft, setDraft] = useState(txn);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const list = draft.type === "income" ? categories.income : categories.expense;

  const save = async () => {
    // Remember the correction, so the same merchant categorises itself next time.
    if (draft.category !== txn.category && draft.description) {
      const key = draft.description.toLowerCase().slice(0, 28).trim();
      const rules = setting(SETTING_IDS.categoryRules, {});
      await patchSetting(SETTING_IDS.categoryRules, { ...rules, [key]: draft.category });
    }
    await put("transaction", { ...draft, amount: Number(draft.amount), updatedAt: Date.now() }, txn.id);
    toast.success("Transaction updated");
    onClose();
  };

  return (
    <>
      <Modal title="Edit transaction" onClose={onClose}
        footer={
          <>
            <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
              <TrashIcon size={15} /> Delete
            </button>
            <div className="grow" />
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </>
        }>
        <div className="field">
          <label htmlFor="t-desc">Description</label>
          <input id="t-desc" type="text" value={draft.description}
            onChange={(e) => update({ description: e.target.value })} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="t-date">Date</label>
            <input id="t-date" type="date" value={draft.date} onChange={(e) => update({ date: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="t-amount">Amount ({currency})</label>
            <input id="t-amount" type="number" step="0.01" value={draft.amount}
              onChange={(e) => update({ amount: e.target.value })} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="t-type">Type</label>
            <select id="t-type" value={draft.type}
              onChange={(e) => update({
                type: e.target.value,
                category: e.target.value === "income" ? categories.income[0] : categories.expense[0],
              })}>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="t-cat">Category</label>
            <select id="t-cat" value={draft.category} onChange={(e) => update({ category: e.target.value })}>
              {list.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="t-inst">Institution</label>
            <input id="t-inst" type="text" value={draft.institution || ""}
              onChange={(e) => update({ institution: e.target.value })} />
          </div>
        </div>
        <p className="hint-text">
          Changing the category teaches AIDIA — transactions from the same merchant will
          use it automatically next time.
        </p>
      </Modal>

      {confirmDelete && (
        <ConfirmDialog title="Delete this transaction?" danger confirmLabel="Delete"
          message="It will be removed from every device you sync with."
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => { await remove(txn.id); toast.success("Transaction deleted"); onClose(); }} />
      )}
    </>
  );
}

function CategoryManager({ onClose }) {
  const { setting, patchSetting, SETTING_IDS } = useVault();
  const toast = useToast();
  const current = setting(SETTING_IDS.categories, null) || Finance.defaultCategories();
  const [cats, setCats] = useState(current);
  const [adding, setAdding] = useState({ expense: "", income: "" });

  const add = (type) => {
    const v = adding[type].trim();
    if (!v || cats[type].includes(v)) return;
    setCats({ ...cats, [type]: [...cats[type], v] });
    setAdding({ ...adding, [type]: "" });
  };

  const save = async () => {
    await patchSetting(SETTING_IDS.categories, cats);
    toast.success("Categories saved");
    onClose();
  };

  return (
    <Modal title="Manage categories" size="wide" onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={() => setCats(Finance.defaultCategories())}>
            Reset to defaults
          </button>
          <div className="grow" />
          <button className="btn btn-primary" onClick={save}>Save</button>
        </>
      }>
      {["expense", "income"].map((type) => (
        <div className="field" key={type}>
          <label style={{ textTransform: "capitalize" }}>{type} categories</label>
          <div className="row wrap" style={{ gap: "var(--s-2)" }}>
            {cats[type].map((c) => (
              <span className="tag" key={c}>
                {c}
                <button onClick={() => setCats({ ...cats, [type]: cats[type].filter((x) => x !== c) })}
                  aria-label={`Remove ${c}`}>×</button>
              </span>
            ))}
          </div>
          <div className="row" style={{ gap: "var(--s-2)", marginTop: "var(--s-2)" }}>
            <input type="text" value={adding[type]} placeholder={`New ${type} category`}
              onChange={(e) => setAdding({ ...adding, [type]: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(type))} />
            <button className="btn btn-ghost" onClick={() => add(type)}><PlusIcon size={15} /> Add</button>
          </div>
        </div>
      ))}
    </Modal>
  );
}
